import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const JobDetailsModal = ({ job, isOpen, onClose, onSave }) => {
  const [editedJob, setEditedJob] = useState(job || {});
  const [activeTab, setActiveTab] = useState('details');

  if (!isOpen || !job) return null;

  const operatorOptions = [
    { value: '', label: 'Select Operator' },
    { value: 'rajesh-kumar', label: 'Rajesh Kumar' },
    { value: 'priya-sharma', label: 'Priya Sharma' },
    { value: 'amit-patel', label: 'Amit Patel' },
    { value: 'sunita-devi', label: 'Sunita Devi' },
    { value: 'vikram-singh', label: 'Vikram Singh' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on-hold', label: 'On Hold' }
  ];

  const tabs = [
    { id: 'details', label: 'Job Details', icon: 'FileText' },
    { id: 'progress', label: 'Progress', icon: 'TrendingUp' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  const activityHistory = [
    {
      id: 1,
      action: 'Job Created',
      user: 'Production Manager',
      timestamp: '2025-01-26 08:30:00',
      details: 'New production job created for Floral Summer Collection'
    },
    {
      id: 2,
      action: 'Operator Assigned',
      user: 'Supervisor',
      timestamp: '2025-01-26 09:15:00',
      details: 'Assigned to Rajesh Kumar for digital printing'
    },
    {
      id: 3,
      action: 'Progress Updated',
      user: 'Rajesh Kumar',
      timestamp: '2025-01-26 10:45:00',
      details: 'Digital printing 75% completed'
    },
    {
      id: 4,
      action: 'Issue Reported',
      user: 'Quality Control',
      timestamp: '2025-01-26 11:20:00',
      details: 'Minor color variation detected, adjustments made'
    }
  ];

  const handleInputChange = (field, value) => {
    setEditedJob(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSizeDistributionChange = (size, value) => {
    setEditedJob(prev => ({
      ...prev,
      sizeDistribution: {
        ...prev.sizeDistribution,
        [size]: parseInt(value) || 0
      }
    }));
  };

  const handleSave = () => {
    onSave(editedJob);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Job Details</h2>
              <p className="text-sm text-muted-foreground">#{job.jobId} - {job.designName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={20}
            onClick={onClose}
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-smooth ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Job ID"
                  value={editedJob.jobId}
                  disabled
                  className="bg-muted/50"
                />
                <Input
                  label="Design Name"
                  value={editedJob.designName}
                  onChange={(e) => handleInputChange('designName', e.target.value)}
                />
                <Input
                  label="Fabric Required (meters)"
                  type="number"
                  value={editedJob.fabricMeters}
                  onChange={(e) => handleInputChange('fabricMeters', e.target.value)}
                />
                <Input
                  label="Total Pieces"
                  type="number"
                  value={editedJob.totalPieces}
                  onChange={(e) => handleInputChange('totalPieces', e.target.value)}
                />
                <Select
                  label="Priority"
                  options={priorityOptions}
                  value={editedJob.priority}
                  onChange={(value) => handleInputChange('priority', value)}
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={editedJob.status}
                  onChange={(value) => handleInputChange('status', value)}
                />
                <Input
                  label="Due Date"
                  type="date"
                  value={editedJob.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                />
                <Select
                  label="Assigned Operator"
                  options={operatorOptions}
                  value={editedJob.assignedOperator}
                  onChange={(value) => handleInputChange('assignedOperator', value)}
                  searchable
                />
              </div>

              {/* Size Distribution */}
              <div>
                <h4 className="font-medium text-foreground mb-3">Size Distribution</h4>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(editedJob.sizeDistribution || {}).map(([size, count]) => (
                    <div key={size}>
                      <Input
                        label={size}
                        type="number"
                        value={count}
                        onChange={(e) => handleSizeDistributionChange(size, e.target.value)}
                        min="0"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    value={editedJob.instructions || ''}
                    onChange={(e) => handleInputChange('instructions', e.target.value)}
                    placeholder="Enter any special instructions..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Quality Notes
                  </label>
                  <textarea
                    className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    value={editedJob.qualityNotes || ''}
                    onChange={(e) => handleInputChange('qualityNotes', e.target.value)}
                    placeholder="Quality control notes..."
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-6">
              {/* Progress Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Overall Progress</span>
                    <span className="text-lg font-semibold text-foreground">{job.progress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${job.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Time Elapsed</span>
                    <span className="text-lg font-semibold text-foreground">2.5h</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Started at 08:30 AM</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Est. Completion</span>
                    <span className="text-lg font-semibold text-foreground">4:30 PM</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Today</p>
                </div>
              </div>

              {/* Stage Progress */}
              <div>
                <h4 className="font-medium text-foreground mb-4">Stage Progress</h4>
                <div className="space-y-4">
                  {[
                    { stage: 'Digital Printing', progress: 100, status: 'completed' },
                    { stage: 'Cutting', progress: 75, status: 'in-progress' },
                    { stage: 'Stitching', progress: 0, status: 'pending' }
                  ].map((stage) => (
                    <div key={stage.stage} className="flex items-center space-x-4">
                      <div className="w-24 text-sm text-muted-foreground">{stage.stage}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            stage.status === 'completed' ? 'bg-success/10 text-success' :
                            stage.status === 'in-progress'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                          }`}>
                            {stage.status.replace('-', ' ')}
                          </span>
                          <span className="text-sm font-medium text-foreground">{stage.progress}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              stage.status === 'completed' ? 'bg-success' :
                              stage.status === 'in-progress' ? 'bg-warning' : 'bg-muted'
                            }`}
                            style={{ width: `${stage.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Defects & Issues */}
              {job.defects > 0 && (
                <div className="bg-error/5 border border-error/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                    <h4 className="font-medium text-error">Quality Issues</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {job.defects} defective pieces identified during cutting process
                  </p>
                  <Button variant="outline" size="sm" iconName="Eye" iconSize={14}>
                    View Details
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Activity Timeline</h4>
              <div className="space-y-4">
                {activityHistory.map((activity, index) => (
                  <div key={activity.id} className="flex space-x-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon name="Clock" size={14} />
                      </div>
                      {index < activityHistory.length - 1 && (
                        <div className="w-px h-8 bg-border mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-medium text-foreground">{activity.action}</h5>
                        <span className="text-xs text-muted-foreground">
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{activity.details}</p>
                      <p className="text-xs text-muted-foreground">by {activity.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;